/**
 * Generato automaticamente.
 * framework-cfg-procedimenti
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  CreaTipoPagamentoRequest,
  GetTipiPagamentoParams,
  TipoPagamentoDTO,
  TipoPagamentoPdfDTO,
  TipoPagamentoPdfRequest,
  WsProblema,
  WsRispostaPaginataListTipoPagamentoDTO,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Restituisce il dettaglio di un tipo pagamento
 * @summary Restituisce tipo pagamento
 */
export const getTipiPagamentoId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<TipoPagamentoDTO>(
    {
      url: `/framework-cfg-procedimenti/tipi-pagamento/${id}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetTipiPagamentoIdQueryKey = (id: string) =>
  [`/framework-cfg-procedimenti/tipi-pagamento/${id}`] as const;

export const getGetTipiPagamentoIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getTipiPagamentoId>>,
  TError = ErrorType<string>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiPagamentoId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getTipiPagamentoId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTipiPagamentoIdQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTipiPagamentoId>>
  > = ({ signal }) => getTipiPagamentoId(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetTipiPagamentoIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTipiPagamentoId>>
>;
export type GetTipiPagamentoIdQueryError = ErrorType<string>;

/**
 * @summary Restituisce tipo pagamento
 */
export const useGetTipiPagamentoId = <
  TData = Awaited<ReturnType<typeof getTipiPagamentoId>>,
  TError = ErrorType<string>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiPagamentoId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTipiPagamentoIdQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Aggiorna tipo pagamento
 * @summary Aggiorna tipo pagamento
 */
export const putTipiPagamentoId = (
  id: string,
  creaTipoPagamentoRequest: CreaTipoPagamentoRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<TipoPagamentoDTO>(
    {
      url: `/framework-cfg-procedimenti/tipi-pagamento/${id}`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: creaTipoPagamentoRequest,
    },
    options,
  );
};

export const getPutTipiPagamentoIdMutationOptions = <
  TError = ErrorType<string>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putTipiPagamentoId>>,
    TError,
    { id: string; data: CreaTipoPagamentoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putTipiPagamentoId>>,
  TError,
  { id: string; data: CreaTipoPagamentoRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putTipiPagamentoId>>,
    { id: string; data: CreaTipoPagamentoRequest }
  > = props => {
    const { id, data } = props ?? {};

    return putTipiPagamentoId(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutTipiPagamentoIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof putTipiPagamentoId>>
>;
export type PutTipiPagamentoIdMutationBody = CreaTipoPagamentoRequest;
export type PutTipiPagamentoIdMutationError = ErrorType<string>;

/**
 * @summary Aggiorna tipo pagamento
 */
export const usePutTipiPagamentoId = <
  TError = ErrorType<string>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putTipiPagamentoId>>,
    TError,
    { id: string; data: CreaTipoPagamentoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutTipiPagamentoIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Cancellazione tipo pagamento
 * @summary Cancellazione tipo pagamento
 */
export const deleteTipiPagamentoId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cfg-procedimenti/tipi-pagamento/${id}`,
      method: 'delete',
    },
    options,
  );
};

export const getDeleteTipiPagamentoIdMutationOptions = <
  TError = ErrorType<string>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteTipiPagamentoId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteTipiPagamentoId>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteTipiPagamentoId>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return deleteTipiPagamentoId(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteTipiPagamentoIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteTipiPagamentoId>>
>;

export type DeleteTipiPagamentoIdMutationError = ErrorType<string>;

/**
 * @summary Cancellazione tipo pagamento
 */
export const useDeleteTipiPagamentoId = <
  TError = ErrorType<string>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteTipiPagamentoId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getDeleteTipiPagamentoIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Ottiene la struttura della configurazione PDF del tipo pagamento.
 * @summary Get tipo pagamento configurazione pdf
 */
export const getTipiPagamentoIdPdf = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<TipoPagamentoPdfDTO>(
    {
      url: `/framework-cfg-procedimenti/tipi-pagamento/${id}/pdf`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetTipiPagamentoIdPdfQueryKey = (id: string) =>
  [`/framework-cfg-procedimenti/tipi-pagamento/${id}/pdf`] as const;

export const getGetTipiPagamentoIdPdfQueryOptions = <
  TData = Awaited<ReturnType<typeof getTipiPagamentoIdPdf>>,
  TError = ErrorType<WsProblema>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiPagamentoIdPdf>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getTipiPagamentoIdPdf>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetTipiPagamentoIdPdfQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTipiPagamentoIdPdf>>
  > = ({ signal }) => getTipiPagamentoIdPdf(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetTipiPagamentoIdPdfQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTipiPagamentoIdPdf>>
>;
export type GetTipiPagamentoIdPdfQueryError = ErrorType<WsProblema>;

/**
 * @summary Get tipo pagamento configurazione pdf
 */
export const useGetTipiPagamentoIdPdf = <
  TData = Awaited<ReturnType<typeof getTipiPagamentoIdPdf>>,
  TError = ErrorType<WsProblema>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiPagamentoIdPdf>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTipiPagamentoIdPdfQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Modifica la struttura della configurazione PDF del tipo pagamento.
 * @summary Modifica tipo pagamento configurazione pdf
 */
export const putTipiPagamentoIdPdf = (
  id: string,
  tipoPagamentoPdfRequest: TipoPagamentoPdfRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<TipoPagamentoPdfDTO>(
    {
      url: `/framework-cfg-procedimenti/tipi-pagamento/${id}/pdf`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: tipoPagamentoPdfRequest,
    },
    options,
  );
};

export const getPutTipiPagamentoIdPdfMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putTipiPagamentoIdPdf>>,
    TError,
    { id: string; data: TipoPagamentoPdfRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putTipiPagamentoIdPdf>>,
  TError,
  { id: string; data: TipoPagamentoPdfRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putTipiPagamentoIdPdf>>,
    { id: string; data: TipoPagamentoPdfRequest }
  > = props => {
    const { id, data } = props ?? {};

    return putTipiPagamentoIdPdf(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutTipiPagamentoIdPdfMutationResult = NonNullable<
  Awaited<ReturnType<typeof putTipiPagamentoIdPdf>>
>;
export type PutTipiPagamentoIdPdfMutationBody = TipoPagamentoPdfRequest;
export type PutTipiPagamentoIdPdfMutationError = ErrorType<WsProblema>;

/**
 * @summary Modifica tipo pagamento configurazione pdf
 */
export const usePutTipiPagamentoIdPdf = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putTipiPagamentoIdPdf>>,
    TError,
    { id: string; data: TipoPagamentoPdfRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutTipiPagamentoIdPdfMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Ricerca paginata di tipi pagamento con filtri
 * @summary Ricerca paginata tipi pagamento
 */
export const getTipiPagamento = (
  params?: GetTipiPagamentoParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListTipoPagamentoDTO>(
    {
      url: `/framework-cfg-procedimenti/tipi-pagamento`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetTipiPagamentoQueryKey = (params?: GetTipiPagamentoParams) =>
  [
    `/framework-cfg-procedimenti/tipi-pagamento`,
    ...(params ? [params] : []),
  ] as const;

export const getGetTipiPagamentoQueryOptions = <
  TData = Awaited<ReturnType<typeof getTipiPagamento>>,
  TError = ErrorType<string>,
>(
  params?: GetTipiPagamentoParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiPagamento>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getTipiPagamento>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetTipiPagamentoQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTipiPagamento>>
  > = ({ signal }) => getTipiPagamento(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetTipiPagamentoQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTipiPagamento>>
>;
export type GetTipiPagamentoQueryError = ErrorType<string>;

/**
 * @summary Ricerca paginata tipi pagamento
 */
export const useGetTipiPagamento = <
  TData = Awaited<ReturnType<typeof getTipiPagamento>>,
  TError = ErrorType<string>,
>(
  params?: GetTipiPagamentoParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipiPagamento>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTipiPagamentoQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Crea tipo pagamento se non esiste
 * @summary Creazione tipo pagamento
 */
export const postTipiPagamento = (
  creaTipoPagamentoRequest: CreaTipoPagamentoRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<TipoPagamentoDTO>(
    {
      url: `/framework-cfg-procedimenti/tipi-pagamento`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: creaTipoPagamentoRequest,
    },
    options,
  );
};

export const getPostTipiPagamentoMutationOptions = <
  TError = ErrorType<string>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postTipiPagamento>>,
    TError,
    { data: CreaTipoPagamentoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postTipiPagamento>>,
  TError,
  { data: CreaTipoPagamentoRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postTipiPagamento>>,
    { data: CreaTipoPagamentoRequest }
  > = props => {
    const { data } = props ?? {};

    return postTipiPagamento(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostTipiPagamentoMutationResult = NonNullable<
  Awaited<ReturnType<typeof postTipiPagamento>>
>;
export type PostTipiPagamentoMutationBody = CreaTipoPagamentoRequest;
export type PostTipiPagamentoMutationError = ErrorType<string>;

/**
 * @summary Creazione tipo pagamento
 */
export const usePostTipiPagamento = <
  TError = ErrorType<string>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postTipiPagamento>>,
    TError,
    { data: CreaTipoPagamentoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostTipiPagamentoMutationOptions(options);

  return useMutation(mutationOptions);
};
